<template>
  <aside v-if="filteredMenuList.length > 0" class="active">
    <button type="button" class="toggle-aside"></button>
    <a href="#" class="link-dashboard">메인 대시보드</a>
    <dl class="toggle-box2">
      <template v-for="(menu, index) in filteredMenuList" :key="menu.id">
        <div class="menu-item">
          <input
            type="checkbox"
            :id="'menu-item-' + index"
            class="menu-toggle"
            style="display: none"
          />
          <label
            :for="menu.disabled ? '' : 'menu-item-' + index"
            class="menu-label"
            :class="{ 'menu-disabled': menu.disabled }"
            @click="menu.disabled && $event.preventDefault()"
          >
            <dt>
              {{ menu.name }}
            </dt>
          </label>
          <dd class="menu-content">
            <template v-if="menu.subMenu && menu.subMenu.length">
              <template
                v-for="(subMenu, subMenuIndex) in menu.subMenu"
                :key="subMenuIndex"
              >
                <router-link
                  :to="subMenu.disabled ? '#' : subMenu.url"
                  :class="{ 'menu-loading': subMenu.disabled }"
                  @click="subMenu.disabled && $event.preventDefault()"
                >
                  {{ subMenu.name }}
                  <span v-if="subMenu.disabled" class="loading-indicator"
                    >...</span
                  >
                </router-link>
              </template>
            </template>
          </dd>
        </div>
      </template>
    </dl>
  </aside>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { usePermissionsStore } from '@/store/permissions'

const permissionsStore = usePermissionsStore()
const isPermissionsLoaded = ref(false)

// 메뉴 코드와 매핑된 전체 메뉴 정의
const fullMenuList = [
  {
    name: '컨텐츠 관리',
    subMenu: [
      { name: '배너 관리', url: '/content/banner', permissionCode: 'BANNER' },
      {
        name: '월간 콘텐츠 관리',
        url: '/content/monthly-contents',
        permissionCode: 'MONTHLY_CONTENT',
      },
      {
        name: '스페셜 관리',
        url: '/article/special',
        permissionCode: 'SPECIAL',
      },
      {
        name: '전시 관리',
        url: '/article/exhibitions',
        permissionCode: 'EXHIBITION',
      },
      {
        // 전시를 만드는 기능이라 전시 관리와 같은 권한 코드를 쓴다 (mimio#71).
        name: '전시 크롤링',
        url: '/content/exhibition-crawl',
        permissionCode: 'EXHIBITION',
      },
      { name: '이벤트 관리', url: '/article/events', permissionCode: 'EVENT' },
      { name: '뉴스 관리', url: '/article/news', permissionCode: 'NEWS' },
      {
        name: '공모전 관리',
        url: '/content/contest',
        permissionCode: 'CONTEST',
      },
      {
        name: '작가/작품 검색',
        url: '/content/artist-search',
        permissionCode: 'ARTWORK_SEARCH',
      },
    ],
  },
  {
    name: '회원 관리',
    subMenu: [
      { name: '회원 관리', url: '/user', permissionCode: 'USER' },
      { name: '아티스트 관리', url: '/user/artist', permissionCode: 'ARTIST' },
      {
        name: '아티스트 승인 관리',
        url: '/user/approval',
        permissionCode: 'ARTIST_APPROVAL',
      },
      {
        name: '아티스트 노출 관리',
        url: '/content/artist',
        permissionCode: 'ARTIST_EXPOSURE',
      },
    ],
  },
  {
    name: '운영 관리',
    subMenu: [
      {
        name: '공지사항 관리',
        url: '/support/notice',
        permissionCode: 'NOTICE',
      },
      {
        name: '문의사항 관리',
        url: '/support/inquiry',
        permissionCode: 'INQUIRY',
      },
      {
        name: '협업 신청 관리',
        url: '/support/collaboration',
        permissionCode: 'COLLABORATION',
      },
      {
        name: '서비스 소개 관리',
        url: '/info/service',
        permissionCode: 'SERVICE_INTRODUCTION',
      },
    ],
  },
  {
    name: '기업 관리',
    subMenu: [
      {
        name: '기업 관리',
        url: '/organization',
        permissionCode: 'ORGANIZATION',
      },
      {
        name: '기업 계정 관리',
        url: '/organization/account',
        permissionCode: 'ADMIN',
      },
    ],
  },
  {
    name: '시스템 관리',
    subMenu: [
      {
        name: '관리자 계정 관리',
        url: '/system/admin',
        permissionCode: 'ADMIN',
      },
      {
        name: '역할 권한 관리',
        url: '/system/role',
        permissionCode: 'ADMIN_ROLE',
      },
    ],
  },
  {
    name: '통계',
    subMenu: [
      {
        name: '대시보드',
        url: '/analytics/dashboard',
        permissionCode: 'ANALYTICS',
      },
      {
        name: '회원 통계',
        url: '/analytics/members',
        permissionCode: 'ANALYTICS',
      },
      {
        name: '아티스트·승인',
        url: '/analytics/artists',
        permissionCode: 'ANALYTICS',
      },
      {
        name: '작품·콘텐츠',
        url: '/analytics/content',
        permissionCode: 'ANALYTICS',
      },
      {
        name: '트래픽(GA)',
        url: '/analytics/traffic',
        permissionCode: 'ANALYTICS',
      },
      {
        name: '(x) 조회수 분석',
        url: '/analytics/view-logs',
        permissionCode: 'ANALYTICS',
      },
      {
        name: '(x) 조회수 통계',
        url: '/analytics/view-counts',
        permissionCode: 'ANALYTICS',
      },
    ],
  },
]

// 권한에 따라 필터링된 메뉴 리스트 (성능 최적화)
const filteredMenuList = computed(() => {
  // 권한 정보가 로드되지 않은 경우 상위 메뉴만 표시 (하위 메뉴 숨김)
  if (!isPermissionsLoaded.value || !permissionsStore.isLoaded) {
    return fullMenuList.map(menu => ({
      ...menu,
      subMenu: [], // 하위 메뉴 완전히 숨김
      disabled: true, // 상위 메뉴 클릭 비활성화
    }))
  }

  // 성능 최적화: 반복 계산 최소화
  const menuPermissions = permissionsStore.menuPermissions

  return fullMenuList
    .map(menu => {
      // 각 메뉴 그룹의 서브메뉴를 권한에 따라 필터링
      const filteredSubMenu = menu.subMenu.filter(subMenu => {
        return menuPermissions[subMenu.permissionCode]?.canRead || false
      })

      return {
        ...menu,
        subMenu: filteredSubMenu,
      }
    })
    .filter(menu => menu.subMenu.length > 0) // 서브메뉴가 하나도 없는 메뉴 그룹은 제외
})

// 권한 스토어 상태 변화 감지
watch(
  () => permissionsStore.isLoaded,
  newValue => {
    if (newValue) {
      isPermissionsLoaded.value = true
    }
  },
  { immediate: true }
)

// 컴포넌트 마운트 시 권한 정보 로드 시도
onMounted(async () => {
  try {
    if (!permissionsStore.isLoaded) {
      await permissionsStore.fetchUserPermissions()
    } else {
      // 이미 로딩된 경우 즉시 활성화
      isPermissionsLoaded.value = true
    }

    // 선택적: 실시간 권한 업데이트가 필요한 경우 활성화
    // permissionsStore.startAutoRefresh(60000) // 1분마다
  } catch (error) {
    console.error('권한 정보 로드 실패:', error)
    isPermissionsLoaded.value = false
  }
})

// 컴포넌트 언마운트 시 리소스 정리
onBeforeUnmount(() => {
  permissionsStore.stopAutoRefresh()
})
</script>
<style>
.menu-content {
  display: none;
}

.menu-toggle:checked + .menu-label + .menu-content {
  display: block;
}

.menu-label {
  cursor: pointer;
}

.menu-label.menu-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.menu-loading {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.loading-indicator {
  margin-left: 5px;
  color: #999;
  font-size: 12px;
}
</style>
